import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Debt } from '../../models/debt.model';
import { generatePagination } from '../../utils/pagination.utils';
import { DebtService } from '../../services/debt.service';
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';

@Component({
  selector: 'app-debts',
  standalone: false,
  templateUrl: './debts.component.html',
  styleUrl: './debts.component.css'
})
export class DebtsComponent implements OnInit, OnDestroy {
  @Input() uuid!: string;
  debts: Debt[] = [];
  nextPageUrl: string | null = null;
  previousPageUrl: string | null = null;
  currentPage = 1;
  totalPages = 1;
  pageNumbers: any = [];
  submitted = false;
  total_amount = '0';
  total_remaining_amount = '0';

  isRefundsManager = false;
  selectedDebt: Debt | null = null;
  showClearDebtModal = false;
  isClearingDebt = false;
  clearDebtReason = '';
  clearDebtComment = '';
  toastMessage: string | null = null;
  toastType: 'success' | 'error' = 'success';
  private toastTimeout: any = null;

  constructor(
    private debtService: DebtService,
    private authService: AuthService,
    private logService: LogService,
  ) {}

  get hasActiveDebts(): boolean {
    const remaining = parseFloat(this.total_remaining_amount);
    return (
      (!isNaN(remaining) && remaining > 0) ||
      this.debts.some(
        (d) =>
          d.status === 'PENDING_RECOVERY' || d.status === 'PARTIALLY_RECOVERED',
      )
    );
  }

  canClearDebt(debt: Debt): boolean {
    return (
      this.isRefundsManager &&
      (debt.status === 'PENDING_RECOVERY' || debt.status === 'PARTIALLY_RECOVERED')
    );
  }

  ngOnInit() {
    this.isRefundsManager = this.authService.isRefundsManager();
    this.loadDebts(this.currentPage);
  }

  ngOnDestroy() {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
  }

  reset() {
    this.debts = [];
    this.nextPageUrl = null;
    this.previousPageUrl = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.pageNumbers = [];
  }

  loadDebts(page: number) {
    this.reset();
    this.submitted = true;

    this.debtService.getDebts(this.uuid, page).subscribe(
      response => {
        this.debts = response.results;
        this.nextPageUrl = response.next;
        this.previousPageUrl = response.previous;
        this.currentPage = page;
        this.total_amount = response.total_amount;
        this.total_remaining_amount = response.total_remaining_amount;

        const itemsPerPage = 20;
        this.totalPages = Math.ceil(response.count / itemsPerPage);
        this.pageNumbers = generatePagination(this.currentPage, this.totalPages);
        this.submitted = false;
      }
    );
  }

  openClearDebtModal(debt?: Debt) {
    this.selectedDebt = debt || null;
    this.clearDebtReason = '';
    this.clearDebtComment = '';
    this.showClearDebtModal = true;
  }

  closeClearDebtModal() {
    if (this.isClearingDebt) return;
    this.showClearDebtModal = false;
    this.selectedDebt = null;
    this.clearDebtReason = '';
    this.clearDebtComment = '';
  }

  confirmClearDebt() {
    this.isClearingDebt = true;

    const payload: Record<string, any> = {};
    if (this.clearDebtReason && this.clearDebtReason.trim()) {
      payload['reason'] = this.clearDebtReason.trim();
    }
    if (this.clearDebtComment && this.clearDebtComment.trim()) {
      payload['comment'] = this.clearDebtComment.trim();
    }

    const isSingle = !!this.selectedDebt;
    const request$ = this.selectedDebt
      ? this.debtService.clearDebt(this.uuid, this.selectedDebt.id, payload)
      : this.debtService.clearDebts(this.uuid, payload);

    request$.subscribe({
      next: () => {
        this.isClearingDebt = false;
        this.showClearDebtModal = false;
        this.selectedDebt = null;
        this.clearDebtReason = '';
        this.clearDebtComment = '';

        // Invalidate caches and refetch debts
        this.debtService.invalidateDebtsCache(this.uuid);
        this.logService.invalidateLogsCache(this.uuid);
        this.loadDebts(1);

        this.showToast(
          isSingle ? 'Debt successfully cleared.' : 'Debts successfully cleared.',
          'success',
        );
      },
      error: (err) => {
        this.isClearingDebt = false;
        let msg = isSingle
          ? 'Failed to clear debt. Please try again.'
          : 'Failed to clear debts. Please try again.';
        if (err?.error && typeof err.error.message === 'string') {
          msg = err.error.message;
        } else if (err?.error && Array.isArray(err.error.message)) {
          msg = err.error.message.join(', ');
        } else if (err?.error && typeof err.error.error === 'string') {
          msg = err.error.error;
        } else if (err?.error && typeof err.error.detail === 'string') {
          msg = err.error.detail;
        } else if (
          typeof err?.error === 'string' &&
          err.error.length < 300 &&
          !err.error.includes('<html')
        ) {
          msg = err.error;
        }
        this.showToast(msg, 'error');
      },
    });
  }

  showToast(message: string, type: 'success' | 'error' = 'success') {
    this.toastMessage = message;
    this.toastType = type;
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this.toastTimeout = setTimeout(() => {
      this.toastMessage = null;
    }, 4000);
  }

  goToNextPage() {
    if (this.nextPageUrl) {
      const nextPage = this.getPageNumber(this.nextPageUrl);
      this.loadDebts(nextPage);
    }
  }

  goToPreviousPage() {
    if (this.previousPageUrl) {
      const prevPage = this.getPageNumber(this.previousPageUrl);
      this.loadDebts(prevPage);
    }
  }

  private getPageNumber(url: string): number {
    if (!url) return 1; // fallback if url is null

    const queryString = url.split('?')[1]; // get everything after "?"
    if (!queryString) return 1;

    const params = new URLSearchParams(queryString);
    const page = params.get('page');

    return page ? Number(page) : 1;
  }
}
